import { PlanElement, PlanGroup, describePrescription, describeCircuit } from "../lib/planElements";
import { LibraryCues } from "./LibraryCues";
import { VideoBadge } from "./VideoBadge";

/**
 * The full contents of one plan: every exercise with its prescription.
 *
 * Before this existed there was no way to see what was actually *in* a plan anywhere in the product;
 * the history list showed a title and bare exercise names, so a coach published without being able to
 * check what they were publishing.
 *
 * Coach-private notes used to hang off this component too. They are about the athlete rather than the
 * programme, so they moved to the client's own chart on the client detail page -- see `ClientNotes`.
 * Goals did as well (27 Sep debug notes item 8): a goal is a fact about the athlete, not the plan that
 * happened to be live when it was set, so this component no longer renders them at all -- see
 * `ClientGoals`, which every caller here now renders alongside this one.
 */
export function PlanDetail({
  elements,
  groups = [],
}: {
  elements: PlanElement[];
  /** Circuits over those exercises, if the plan has any. */
  groups?: PlanGroup[];
}) {
  return (
    <div className="plan-detail">
      <p className="form-label">Exercises</p>
      {elements.length === 0 ? (
        <p className="plan-summary">No exercises in this plan.</p>
      ) : (
        <ol className="element-list">
          {elements.map((element, index) => {
            const group = element.groupId ? groups.find((g) => g.id === element.groupId) : undefined;
            const startsGroup = Boolean(group) && elements[index - 1]?.groupId !== element.groupId;
            const prescription = describePrescription(element, Boolean(group));
            return (
              <li key={index} className={group ? "element-row in-circuit" : "element-row"}>
                {/* The circuit's line, once, above the exercise that opens it -- repeating "3 rounds" on
                    each of five exercises would read as five separate three-set exercises. */}
                {startsGroup && group && (
                  <span className="circuit-label">
                    {`${group.name || "Circuit"} — ${describeCircuit(group)}`}
                  </span>
                )}
                <span className="element-tag">E{index + 1}</span>
                <span className="element-name">{element.name}</span>
                {prescription && <span className="element-prescription">{prescription}</span>}
                {/* Library cues first, then anything written for this athlete on top. The library's are
                    read live, so this shows what an athlete opening the plan right now would read. */}
                <LibraryCues entryId={element.libraryEntryId} className="element-notes" />
                {element.notes && <span className="element-notes">{String(element.notes)}</span>}
                {/* Only when there really is one -- see VideoBadge. This was rendered from
                    `libraryEntryId` alone, so every exercise advertised a walkthrough that did not
                    exist (F57). */}
                <VideoBadge entryId={element.libraryEntryId} />
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
